import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16, IcoFileSearch16 } from '@onefootprint/icons';
import type { Investigation } from '@onefootprint/request-types/dashboard';
import { Dropdown } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
type InvestigationTitleProps = {
  investigation: Investigation;
  subInvestigations: Investigation[];
  hasMore: boolean;
  parentId?: string;
  /** The full parent investigation, when loaded, so the parent row can show its name + entity. */
  parentInvestigation?: Investigation;
  /** Open the menu on mount. Useful for snapshots/stories. */
  defaultOpen?: boolean;
};

// A dropdown row for one investigation, shared by the parent and sub-investigation sections. Shows
// the entity's fp_id under the title (when it has an entity) so investigations that share a title
// can still be told apart.
const InvestigationRow = t0 => {
  const $ = _c(11);
  const {
    investigationId,
    title,
    fpId
  } = t0;
  const t1 = `/investigations/${investigationId}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IcoFileSearch16 className="shrink-0 mt-0.75" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== title) {
    t3 = <div className="truncate text-body-2 text-primary">{title}</div>;
    $[1] = title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== fpId) {
    t4 = fpId && <div className="truncate text-snippet-3 text-tertiary">{fpId}</div>;
    $[3] = fpId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <div className="flex items-start gap-3">{t2}<div className="min-w-0 flex flex-col gap-0.5">{t3}{t4}</div></div>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t5) {
    t6 = <Dropdown.Item asChild={true} size="compact" className="h-auto items-start"><Link href={t1}>{t5}</Link></Dropdown.Item>;
    $[8] = t1;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};

// The header's centered title. When the investigation has sub-investigations
// (e.g. a Percy orchestrator's agent runs), the name becomes a dropdown trigger
// listing them; otherwise it's static text.
const InvestigationTitle = t0 => {
  const $ = _c(35);
  const {
    investigation,
    subInvestigations,
    hasMore,
    parentId,
    parentInvestigation,
    defaultOpen
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "subInvestigations"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const [open, setOpen] = useState(!!defaultOpen);
  let t2;
  if ($[1] !== investigation.investigationId || $[2] !== investigation.title || $[3] !== t) {
    t2 = investigation.title ?? t(_temp, {
      id: investigation.investigationId
    });
    $[1] = investigation.investigationId;
    $[2] = investigation.title;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const name = t2;
  const parentInvestigationId = investigation.parentInvestigationId;
  const hasParentInvestigation = !!parentInvestigationId;
  if (subInvestigations.length === 0 && !hasParentInvestigation) {
    let t3;
    if ($[5] !== name) {
      t3 = <span className="text-label-2 font-semibold text-primary truncate max-md:px-1.5">{name}</span>;
      $[5] = name;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const investigationsPath = parentId ? `/investigations?parent_investigation_id=${encodeURIComponent(parentId)}` : null;
  let t3;
  if ($[7] !== t) {
    t3 = t(_temp2);
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cx("group flex items-center gap-1 max-w-full px-1.5 h-10 md:h-7 rounded-default cursor-pointer outline-none transition-colors", "hover:bg-hover focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset");
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== name) {
    t5 = <span className="text-label-2 font-semibold text-primary truncate">{name}</span>;
    $[10] = name;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IcoChevronDown16 className="shrink-0" color="tertiary" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <button type="button" className={t4}>{t5}{t6}</button>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <Dropdown.Trigger asChild={true} ariaLabel={t3} className="max-md:max-w-full">{t7}</Dropdown.Trigger>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== parentInvestigation?.fpId || $[19] !== parentInvestigation?.title || $[20] !== parentInvestigationId || $[21] !== t) {
    t9 = parentInvestigationId && <><Dropdown.GroupTitle>{t(_temp3, {
          count: 1
        })}</Dropdown.GroupTitle><Dropdown.Group><InvestigationRow investigationId={parentInvestigationId} title={parentInvestigation?.title ?? t(_temp4, {
          id: parentInvestigationId
        })} fpId={parentInvestigation?.fpId} /></Dropdown.Group></>;
    $[18] = parentInvestigation?.fpId;
    $[19] = parentInvestigation?.title;
    $[20] = parentInvestigationId;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== hasMore || $[24] !== investigationsPath || $[25] !== subInvestigations || $[26] !== t) {
    t10 = subInvestigations.length > 0 && <><Dropdown.GroupTitle>{subInvestigations.length > 1 || hasMore ? t(_temp5, {
          count: subInvestigations.length
        }) : t(_temp6, {
          count: 1
        })}</Dropdown.GroupTitle><Dropdown.Group>{subInvestigations.map(child => <InvestigationRow key={child.investigationId} investigationId={child.investigationId} title={child.title ?? t(_temp7, {
          id: child.investigationId
        })} fpId={child.fpId} />)}{hasMore && investigationsPath && <Dropdown.Item asChild={true} size="compact"><Link href={investigationsPath} target="_blank" rel="noopener noreferrer">{t(_temp8)}</Link></Dropdown.Item>}</Dropdown.Group></>;
    $[23] = hasMore;
    $[24] = investigationsPath;
    $[25] = subInvestigations;
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = <Dropdown.Portal><Dropdown.Content align="center" className="min-w-[280px] max-h-[360px] overflow-y-auto">{t9}{t10}</Dropdown.Content></Dropdown.Portal>;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== open || $[32] !== t11 || $[33] !== t8) {
    t12 = <Dropdown.Root open={open} onOpenChange={setOpen} modal={false}>{t8}{t11}</Dropdown.Root>;
    $[31] = open;
    $[32] = t11;
    $[33] = t8;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};
export default InvestigationTitle;
function _temp($) {
  return $.fallbackName;
}
function _temp2($_0) {
  return $_0.ariaLabel;
}
function _temp3($_1) {
  return $_1.parentTitle_one;
}
function _temp4($_2) {
  return $_2.fallbackName;
}
function _temp5($_3) {
  return $_3.subTitle_other;
}
function _temp6($_4) {
  return $_4.subTitle_one;
}
function _temp7($_5) {
  return $_5.fallbackName;
}
function _temp8($_6) {
  return $_6.showMore;
}